<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  templateData: {
    type: Object,
    required: true,
  },
  variableMappings: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

// Format time for preview
const currentTime = computed(() => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
});

// Check if header is enabled and has content
const hasHeader = computed(() => {
  return (
    props.templateData.header?.enabled &&
    (props.templateData.header?.text ||
      props.templateData.header?.media?.url ||
      props.templateData.header?.media?.fileUrl ||
      props.templateData.header?.format)
  );
});

const headerType = computed(() => {
  if (!hasHeader.value) return null;
  return props.templateData.header.format?.toLowerCase();
});

const headerUrl = computed(() => {
  // Check for uploaded file URL first (from FileUpload component)
  // then fallback to media.url (from existing templates)
  return (
    props.templateData.header?.media?.fileUrl ||
    props.templateData.header?.media?.url ||
    ''
  );
});

const headerText = computed(() => {
  return props.templateData.header?.text || '';
});

const headerFileName = computed(() => {
  return props.templateData.header?.media?.fileName || 'document.pdf';
});

const headerContentType = computed(() => {
  const contentType = props.templateData.header?.media?.contentType || '';
  // Extract file type from content type (e.g., "application/pdf" -> "PDF")
  if (contentType.includes('pdf')) return 'PDF Document';
  if (contentType.includes('word') || contentType.includes('document'))
    return 'Word Document';
  if (contentType.includes('excel') || contentType.includes('spreadsheet'))
    return 'Excel Document';
  if (
    contentType.includes('powerpoint') ||
    contentType.includes('presentation')
  )
    return 'PowerPoint';
  return 'Document';
});

const locationData = computed(() => {
  return props.templateData.header?.media?.location || null;
});

// Check if footer is enabled
const hasFooter = computed(() => {
  return props.templateData.footer?.enabled && props.templateData.footer?.text;
});

const footerText = computed(() => {
  return props.templateData.footer?.text || '';
});

// Check if there are buttons
const hasButtons = computed(() => {
  return props.templateData.buttons?.length > 0;
});

const buttons = computed(() => {
  return props.templateData.buttons || [];
});

// Replace variables with example values from mappings
const replaceVariablesWithExamples = text => {
  if (!text) return '';

  // Use darker background for better contrast with white text
  const bgColor = 'bg-n-slate-7 dark:bg-n-slate-8';
  const textColor = 'text-white';

  // Match both {{1}}, {{2}} (positional) and {{customer_name}} (named)
  return text.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (match, key) => {
    const mapping = props.variableMappings[key];

    if (!mapping) {
      return `<span class="${textColor} ${bgColor} px-1 py-0.5 rounded">[Variable ${key}]</span>`;
    }

    // For dynamic variables, use fallback_value
    // For static variables, use example_value
    const exampleValue =
      mapping.type === 'static'
        ? mapping.example_value
        : mapping.fallback_value;

    return (
      exampleValue ||
      `<span class="${textColor} ${bgColor} px-1 py-0.5 rounded">[${mapping.source || key}]</span>`
    );
  });
};

// Convert WhatsApp markdown to HTML
const parseMarkdownToHTML = text => {
  if (!text) return '';

  // Escape HTML first
  text = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Bold: *text*
  text = text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');

  // Italic: _text_
  text = text.replace(/_([^_]+)_/g, '<em>$1</em>');

  // Strikethrough: ~text~
  text = text.replace(/~([^~]+)~/g, '<del>$1</del>');

  // Monospace: ```text```
  text = text.replace(
    /```([^`]+)```/g,
    '<code class="bg-gray-100 text-gray-800 px-1 py-0.5 rounded font-mono text-xs">$1</code>'
  );

  // Convert newlines to <br>
  text = text.replace(/\n/g, '<br>');

  return text;
};

// Format body text with markdown and variable replacement
const formattedBodyText = computed(() => {
  let text = props.templateData.body?.text || '';

  // Replace variables with examples/fallbacks
  text = replaceVariablesWithExamples(text);

  // Convert WhatsApp markdown to HTML
  text = parseMarkdownToHTML(text);

  return text;
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start py-8 px-4 bg-n-solid-1 overflow-auto"
  >
    <div class="mb-4">
      <h3 class="text-lg font-semibold text-n-slate-12 text-center">
        {{ t('SETTINGS.TEMPLATES.PREVIEW.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 text-center mt-1">
        {{ t('SETTINGS.TEMPLATES.PREVIEW.DESCRIPTION') }}
      </p>
    </div>

    <!-- Phone Mockup -->
    <div
      class="relative w-80 h-[600px] rounded-3xl border-8 border-n-slate-9 overflow-hidden shadow-2xl whatsapp-bg"
    >
      <!-- Status Bar -->
      <div
        class="flex items-center justify-between px-6 py-2 bg-[#075E54] text-white text-xs"
      >
        <span>{{ currentTime }}</span>
        <div class="flex items-center gap-1">
          <span class="i-lucide-wifi size-3" />
          <span class="i-lucide-signal size-3" />
          <!-- 80% Battery -->
          <svg
            class="size-3"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="2"
              y="6"
              width="18"
              height="12"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
            <path d="M20 10h2v4h-2z" fill="currentColor" />
            <rect
              x="4"
              y="8"
              width="14"
              height="8"
              rx="1"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>

      <!-- WhatsApp Header -->
      <div class="flex items-center gap-3 px-4 py-3 bg-[#075E54] text-white">
        <button class="i-lucide-arrow-left size-5" />
        <div class="flex items-center gap-2 flex-1">
          <div
            class="w-10 h-10 rounded-full bg-n-slate-6 flex items-center justify-center"
          >
            <span class="i-lucide-user size-5 text-n-slate-11" />
          </div>
          <div>
            <div class="font-medium text-sm">
              {{ t('SETTINGS.TEMPLATES.PREVIEW.CONTACT_NAME') }}
            </div>
            <div class="text-xs opacity-80">
              {{ t('SETTINGS.TEMPLATES.PREVIEW.ONLINE_STATUS') }}
            </div>
          </div>
        </div>
        <button class="i-lucide-video size-5" />
        <button class="i-lucide-phone size-5" />
        <button class="i-lucide-more-vertical size-5" />
      </div>

      <!-- Chat Area (with bottom padding for input bar) -->
      <div class="flex-1 p-4 pb-16 overflow-y-auto h-[calc(600px-120px)]">
        <!-- Template Message Bubble -->
        <div class="flex justify-end mb-4">
          <div
            class="bg-white rounded-lg shadow-md max-w-[85%] overflow-hidden"
          >
            <!-- Header Media/Text -->
            <div v-if="hasHeader" class="message-header">
              <!-- Image Header -->
              <div v-if="headerType === 'image'" class="p-1">
                <div class="relative bg-n-slate-2 rounded-md overflow-hidden">
                  <img
                    v-if="headerUrl"
                    :src="headerUrl"
                    class="w-full h-auto max-h-64 object-cover"
                    alt="Header image"
                  />
                  <div
                    v-else
                    class="w-full h-48 flex items-center justify-center bg-n-slate-3"
                  >
                    <span class="i-lucide-image size-12 text-n-slate-9" />
                  </div>
                </div>
              </div>

              <!-- Video Header -->
              <div v-else-if="headerType === 'video'" class="p-1">
                <div class="relative bg-black rounded-md overflow-hidden">
                  <video
                    v-if="headerUrl"
                    :src="headerUrl"
                    class="w-full h-auto max-h-64 object-cover"
                    controls
                    playsinline
                  >
                    Your browser does not support the video tag.
                  </video>
                  <!-- Play button overlay when video hasn't started -->
                  <div
                    v-if="headerUrl"
                    class="absolute inset-0 flex items-center justify-center pointer-events-none"
                  >
                    <div
                      class="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg"
                    >
                      <span class="i-lucide-play size-8 text-[#075E54] ml-1" />
                    </div>
                  </div>
                  <div
                    v-if="!headerUrl"
                    class="w-full h-48 flex items-center justify-center bg-n-slate-3"
                  >
                    <div class="text-center">
                      <span
                        class="i-lucide-play-circle size-12 text-n-slate-9 block mx-auto mb-2"
                      />
                      <span class="text-xs text-n-slate-11">{{
                        t('SETTINGS.TEMPLATES.PREVIEW.VIDEO_PREVIEW')
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Document Header -->
              <div
                v-else-if="headerType === 'document'"
                class="flex items-center gap-3 p-4 bg-[#DCF8C6] border-l-4 border-[#25D366]"
              >
                <div
                  class="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm"
                >
                  <span class="i-lucide-file-text size-6 text-[#25D366]" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-[#000000] truncate">
                    {{ headerFileName }}
                  </div>
                  <div class="text-xs text-[#667781] mt-0.5">
                    {{ headerContentType }}
                  </div>
                </div>
                <span class="i-lucide-download size-5 text-[#667781]" />
              </div>

              <!-- Location Header (if supported) -->
              <div v-else-if="headerType === 'location'" class="p-1">
                <div
                  class="relative bg-n-slate-2 h-48 rounded-md overflow-hidden"
                >
                  <div
                    v-if="
                      locationData &&
                      locationData.latitude &&
                      locationData.longitude
                    "
                    class="w-full h-full"
                  >
                    <iframe
                      :src="`https://www.google.com/maps?q=${locationData.latitude},${locationData.longitude}&output=embed`"
                      width="100%"
                      height="100%"
                      style="border: 0"
                      loading="lazy"
                      referrerpolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  <div
                    v-else
                    class="w-full h-full flex items-center justify-center"
                  >
                    <div class="text-center">
                      <span
                        class="i-lucide-map-pin size-12 text-n-slate-9 block mx-auto mb-2"
                      />
                      <span class="text-xs text-n-slate-11">{{
                        t('SETTINGS.TEMPLATES.PREVIEW.LOCATION_PREVIEW') ||
                        'Location Preview'
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Text Header -->
              <div
                v-else-if="headerType === 'text' && headerText"
                class="px-3 pt-3 pb-1 text-base font-semibold text-[#000000]"
              >
                {{ headerText }}
              </div>
            </div>

            <!-- Body Text -->
            <div
              v-if="templateData.body?.text"
              class="px-3 py-2 text-sm text-[#000000] whitespace-pre-wrap break-words"
              v-html="formattedBodyText"
            />
            <div v-else class="px-3 py-2 text-sm text-n-slate-11 italic">
              {{ t('SETTINGS.TEMPLATES.PREVIEW.EMPTY_BODY') }}
            </div>

            <!-- Footer -->
            <div v-if="hasFooter" class="px-3 pb-2 text-xs text-[#667781]">
              {{ footerText }}
            </div>

            <!-- Timestamp -->
            <div class="px-3 pb-2 text-right">
              <span class="text-xs text-[#667781]">{{ currentTime }}</span>
            </div>

            <!-- Buttons -->
            <div v-if="hasButtons" class="border-t border-[#E9EDEF]">
              <button
                v-for="(btn, index) in buttons"
                :key="index"
                class="w-full px-4 py-2.5 text-sm font-medium transition-colors text-[#00A5F4] hover:bg-[#F5F6F6]"
                :class="[
                  index !== buttons.length - 1
                    ? 'border-b border-[#E9EDEF]'
                    : '',
                  btn.type === 'COPY_CODE'
                    ? 'flex items-center justify-between'
                    : 'flex items-center justify-center gap-2',
                ]"
              >
                <template v-if="btn.type === 'COPY_CODE'">
                  <!-- Copy code button: icon on left, code in center, copy on right -->
                  <span class="i-lucide-copy size-4 flex-shrink-0" />
                  <span
                    class="flex-1 text-center font-mono bg-[#F0F2F5] px-3 py-1 rounded text-[#000000] mx-2"
                  >
                    {{ btn.example || 'OFFER25' }}
                  </span>
                  <span class="i-lucide-copy size-4 flex-shrink-0" />
                </template>
                <template v-else>
                  <span
                    v-if="btn.type === 'URL'"
                    class="i-lucide-link size-4"
                  />
                  <span
                    v-if="btn.type === 'PHONE_NUMBER'"
                    class="i-lucide-phone size-4"
                  />
                  <span
                    v-if="btn.type === 'QUICK_REPLY'"
                    class="i-lucide-corner-down-left size-4"
                  />
                  <span>{{ btn.text }}</span>
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Input Bar (WhatsApp-style with pill containers) -->
      <div
        class="absolute bottom-0 left-0 right-0 px-2 py-2 flex items-center gap-2"
      >
        <!-- Main Input Pill (emoji + input + attach + camera) -->
        <div
          class="flex-1 bg-white rounded-full flex items-center px-2 shadow-sm"
        >
          <!-- Emoji Button -->
          <button
            class="p-2 text-[#54656F] hover:text-[#111B21] transition-colors"
          >
            <span class="i-lucide-smile size-5" />
          </button>

          <!-- Input Field -->
          <div class="flex-1 px-2 py-2.5 text-sm text-[#667781]">
            {{ t('SETTINGS.TEMPLATES.PREVIEW.MESSAGE_PLACEHOLDER') }}
          </div>

          <!-- Attach Button -->
          <button
            class="p-2 text-[#54656F] hover:text-[#111B21] transition-colors"
          >
            <span class="i-lucide-paperclip size-5" />
          </button>

          <!-- Camera Button -->
          <button
            class="p-2 text-[#54656F] hover:text-[#111B21] transition-colors"
          >
            <span class="i-lucide-camera size-5" />
          </button>
        </div>

        <!-- Voice/Mic Button (separate circular button) -->
        <button
          class="w-12 h-12 flex items-center justify-center bg-[#00A884] text-white rounded-full hover:bg-[#06CF9C] transition-colors shadow-sm flex-shrink-0"
        >
          <span class="i-lucide-mic size-5" />
        </button>
      </div>
    </div>

    <!-- Preview Legend -->
    <div class="mt-4 text-xs text-n-slate-11 space-y-1 max-w-80">
      <p>• {{ t('SETTINGS.TEMPLATES.PREVIEW.LEGEND.VARIABLES') }}</p>
      <p>• {{ t('SETTINGS.TEMPLATES.PREVIEW.LEGEND.FORMATTING') }}</p>
    </div>
  </div>
</template>

<style scoped>
/* WhatsApp background pattern - authentic official background */
.whatsapp-bg {
  background-color: #ece5dd;
  background-image: url('/assets/images/whatsapp-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* Custom scrollbar - mobile-style, subtle and minimal */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background 0.2s ease;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* For Firefox */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}
</style>
